import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import {
  Search, X, Package, ShoppingCart, Users, Briefcase,
  ClipboardList, ChefHat, Clock, ArrowRight, Loader2
} from 'lucide-react';

const ICON_MAP = {
  Package, ShoppingCart, Users, Briefcase, ClipboardList, ChefHat, Clock, ArrowRight,
};

const TYPE_CONFIG = {
  inventory: { icon: Package, label: 'Inventory', color: 'text-emerald-500', bg: 'bg-emerald-50', path: '/inventory' },
  sales_orders: { icon: ShoppingCart, label: 'Sales Orders', color: 'text-blue-500', bg: 'bg-blue-50', path: '/sales' },
  suppliers: { icon: Users, label: 'Suppliers', color: 'text-purple-500', bg: 'bg-purple-50', path: '/suppliers' },
  employees: { icon: Briefcase, label: 'Employees', color: 'text-orange-500', bg: 'bg-orange-50', path: '/hr/employees' },
  purchase_orders: { icon: ClipboardList, label: 'Purchase Orders', color: 'text-cyan-500', bg: 'bg-cyan-50', path: '/purchase-orders' },
  recipes: { icon: ChefHat, label: 'Recipes', color: 'text-amber-500', bg: 'bg-amber-50', path: '/recipes' },
  customers: { icon: Users, label: 'Customers', color: 'text-pink-500', bg: 'bg-pink-50', path: '/sales' },
};

function addRecentItem(item) {
  try {
    const stored = JSON.parse(localStorage.getItem('office_suite_recent_items') || '[]');
    const filtered = stored.filter(r => !(r.id === item.id && r.type === item.type));
    filtered.unshift({ ...item, viewedAt: new Date().toISOString() });
    localStorage.setItem('office_suite_recent_items', JSON.stringify(filtered.slice(0, 10)));
    window.dispatchEvent(new Event('recentItemsUpdated'));
  } catch {}
}

function GlobalSearchModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    if (!isOpen) {
      setQuery('');
      setResults(null);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ux/search-global`, { params: { q, limit: 25 } });
      setResults(res.data);
      setSelectedIndex(0);
    } catch {
      setResults({ results: [], grouped: {}, total: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, doSearch]);

  const flatResults = results?.results || [];

  const handleSelect = (item) => {
    const config = TYPE_CONFIG[item.type];
    addRecentItem({ id: item.id, type: item.type, name: item.name, path: config?.path || '/', icon: config?.icon?.name });
    const path = config?.path || '/';
    navigate(`${path}?search=${encodeURIComponent(item.name)}`);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatResults[selectedIndex]) {
      handleSelect(flatResults[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  const grouped = results?.grouped || {};

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh]" onClick={onClose}>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden mx-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200">
          <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search inventory, orders, suppliers, employees, recipes..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 text-base text-slate-900 placeholder-slate-400 outline-none bg-transparent"
          />
          {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
          <kbd className="hidden sm:inline-flex items-center px-2 py-1 text-xs text-slate-400 bg-slate-100 rounded-md border border-slate-200">ESC</kbd>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {!query && (
            <div className="px-5 py-8 text-center">
              <Search className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Type to search across all modules</p>
              <p className="text-xs text-slate-400 mt-1">Inventory · Sales · Suppliers · Employees · POs · Recipes</p>
            </div>
          )}

          {query && results && flatResults.length === 0 && !loading && (
            <div className="px-5 py-8 text-center">
              <p className="text-sm text-slate-500">No results found for "{query}"</p>
              <p className="text-xs text-slate-400 mt-1">Try different keywords</p>
            </div>
          )}

          {Object.keys(grouped).length > 0 && (
            <div className="py-2">
              {Object.entries(grouped).map(([type, group]) => {
                const config = TYPE_CONFIG[type] || { icon: Package, label: type, color: 'text-slate-500', bg: 'bg-slate-50' };
                const Icon = config.icon;
                return (
                  <div key={type}>
                    <div className="px-5 py-2 flex items-center gap-2">
                      <Icon className={`w-3.5 h-3.5 ${config.color}`} />
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{config.label}</span>
                      <span className="text-xs text-slate-400 ml-1">({group.count})</span>
                    </div>
                    {group.items.map((item, idx) => {
                      const globalIdx = flatResults.findIndex(r => r.id === item.id && r.type === type);
                      const isSelected = globalIdx === selectedIndex;
                      return (
                        <button
                          key={`${type}-${item.id}-${idx}`}
                          onClick={() => handleSelect({ ...item, type })}
                          className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors ${
                            isSelected ? 'bg-emerald-50 border-l-2 border-emerald-500' : 'hover:bg-slate-50 border-l-2 border-transparent'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${config.bg}`}>
                            <Icon className={`w-4 h-4 ${config.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{item.name}</p>
                            {item.subtitle && <p className="text-xs text-slate-500 truncate">{item.subtitle}</p>}
                          </div>
                          <ArrowRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400 bg-slate-50">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px]">↑↓</kbd> Navigate</span>
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-white rounded border text-[10px]">↵</kbd> Select</span>
          </div>
          {results && <span>{results.total} result{results.total !== 1 ? 's' : ''}</span>}
        </div>
      </div>
    </div>
  );
}

export { addRecentItem };
export default GlobalSearchModal;
